import React from 'react';
import { Announcement, Icon, Modal, notification } from '@block/plug';
import { cloneDeep } from '@block/utils';
import { handleAdapterTopDegree, handleAdapterData } from '@src/utils/educationUtils';
import Url from './url';
import { doAction } from '../../utils';
import EducationCard from './elements/educationCard';
import Supplementary from './components/Supplementary';
import { degreeTemplate, degreeInit } from './template';
import { EducationTypeDictIndexMap } from './consts';
// 导入校验器模块
import { validateMultiEducations, validateTimeOverlap } from './validators';
import { SupplementaryFile } from './types';
import './index.scss';

// educationKey后期换个方式处理
let educationKey = '';

interface Props {
  ref: any;
}

interface State {
  isIntern: boolean; // 是否为实习生
  degree: any[]; // 一段或多段学历
  refs: any[]; // 存储教育经历的ref集合
  supplementAttachList: SupplementaryFile[]; // 补充材料文件列表
  // 无法上传原因列表
  unableReasonList?: any[];
  employeeType: string; // 员工类型
}

class EducationInfo extends React.Component<Props, State> {
  educationCardRef: React.RefObject<any>;

  constructor(props: Props) {
    super(props);
    this.state = {
      isIntern: false, // 是否为实习生
      degree: [], // 一段或多段学历
      refs: [], // 存储教育经历的ref集合
      supplementAttachList: [], // 补充材料文件列表
      unableReasonList: [], // 无法上传原因列表
      employeeType: '', // 员工类型
    };
  }

  /**
   * 组件挂载后获取信息并初始化状态
   */
  async componentDidMount() {
    // V3.0 需求升级获取数据接口为V2版本
    const data = (await doAction(Url.getInfoV2, {}, null)) || {};

    // 处理在途数据
    const adapterData = handleAdapterData(data);
    const { degree, supplementAttachList, isIntern, employeeType } = adapterData;

    // 接口获取所有原因列表
    const unableReasonList = await doAction(Url.getUnableReason, {}).then((res) => res?.pageList || []);

    let tempDegree = null;
    if (degree && degree.length > 0) {
      tempDegree = degree;
    } else {
      // 一段学历都没有的时候，初始化学历，默认最高学历
      tempDegree = degreeInit;
    }

    // 一次性更新状态，避免多次setState
    this.setState({
      degree: tempDegree,
      isIntern, // 使用从接口获取的实习生状态
      supplementAttachList,
      unableReasonList,
      employeeType,
    });
  }

  /**
   * 添加新的学历
   */
  handleAdd = () => {
    const { degree } = this.state;
    degree.push(degreeTemplate);
    this.setState({
      degree,
    });
  };

  /**
   * 删除指定索引的学历
   * @param index 要删除的学历索引
   */
  handleDelete = async (index) => {
    try {
      // 弹窗请求用户确认是否确认删除
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          message: `确认删除吗？`,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      // 用户取消操作，恢复原始状态并返回
      if (!confirmed) {
        return;
      }
    } catch (error) {
      return;
    }

    const { degree } = this.state;
    const tempDegree = cloneDeep(degree);
    tempDegree.splice(index, 1);
    educationKey = educationKey === '' ? ' ' : '';
    this.setState({
      degree: tempDegree,
    });
  };

  /**
   * 处理学历信息变更的核心函数
   *
   * @description
   * 该函数处理学历信息的所有变更操作，主要包括：
   * 1. 更新基本信息：学校、专业、学位等
   * 2. 处理最高学历的特殊逻辑：
   *    - 检查是否存在同类型的最高学历
   *    - 如果存在，弹窗确认是否覆盖
   *    - 更新相关记录的最高学历标记
   *
   * @param newDegreeData - 变更后的学历记录数据
   * @param targetIndex - 被变更记录的索引
   *
   * @flow
   * 1. 如果不涉及最高学历变更，直接更新状态
   * 2. 如果设置为最高学历：
   *    a. 检查并更新所有相关记录
   *    b. 如果存在冲突，请求用户确认
   *    c. 根据用户选择更新状态
   *
   * @throws {Error} 可能在Modal交互过程中抛出错误
   */
  handleChange = async (newDegreeData, targetIndex) => {
    // 获取当前所有学历记录
    const { degree } = this.state;

    // 如果数据没有变化，直接返回
    if (JSON.stringify(degree?.[targetIndex]) === JSON.stringify(newDegreeData)) {
      return;
    }

    // 如果是时间改变，才进行提示
    if (
      newDegreeData?.startDate !== degree?.[targetIndex]?.startDate ||
      newDegreeData?.endDate !== degree?.[targetIndex]?.endDate
    ) {
      // 校验时间重叠
      validateTimeOverlap(degree, newDegreeData, targetIndex);
    }

    // 解构新数据中的关键信息
    const { isTopDegree, educationTypeDictIndex: newEducationType } = newDegreeData;

    // 场景1: 非最高学历的普通更新
    if (!isTopDegree) {
      // 创建新的学历数组并更新指定记录
      const updatedDegrees = [...degree];
      updatedDegrees[targetIndex] = newDegreeData;
      this.setState({ degree: updatedDegrees });
      return;
    }

    // 场景2: 最高学历的特殊处理
    // 标记是否存在同类型的最高学历
    let hasExistingTopDegree = false;
    // 处理所有学历记录
    const updatedDegrees = degree.map((degreeRecord, recordIndex) => {
      // 解构当前记录的关键信息
      const { educationTypeDictIndex: currentEducationType, isTopDegree: currentIsTopDegree } = degreeRecord;

      // 处理当前正在编辑的记录
      if (recordIndex === targetIndex) {
        return newDegreeData;
      }

      // 处理其他的教育经历 recordIndex !== targetIndex，所以一定是不同的教育经历

      // 判断是否为同类型教育（全日制/非全日制）
      const isSameEducationType = currentEducationType === newEducationType;
      // 当前一定是 isTopDegree 为 true
      if (isSameEducationType) {
        // 检查是否存在同类型的最高学历记录
        if (currentIsTopDegree) {
          // 当前修改下标的教育经历的 isTopDegree 为 true，遍历的 isCurrentTopDegree 又为 true
          // 出现了两段相同类型的最高教育经历，所以有冲突，先标记，后续弹窗确认
          hasExistingTopDegree = true;
        }

        return {
          ...degreeRecord,
          // 处理同类型记录：清除其他同类型的最高学历标记
          isTopDegree: false,
        };
      }

      // 其他类型记录保持不变，比如有最高全日制学历，但是本次新增的是最高非全日制学历
      return degreeRecord;
    });

    // 处理最高学历冲突情况
    if (hasExistingTopDegree) {
      try {
        // 弹窗请求用户确认是否覆盖已有的最高学历
        const confirmed = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            message: `是否要将此教育经历勾选为最高${EducationTypeDictIndexMap?.[newEducationType] || ''}教育经历`,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });

        // 用户取消操作，恢复原始状态并返回
        if (!confirmed) {
          this.setState({ degree });
          return;
        }
      } catch (error) {
        // 发生错误时，恢复原始状态并返回
        console.error('最高学历确认过程发生错误:', error);
        this.setState({ degree });
        return;
      }
    }

    // 更新最终的学历记录状态
    this.setState({
      degree: updatedDegrees,
    });
  };

  /**
   * 校验单个教育经历
   * @returns 校验是否通过
   */
  validateItems = async () => {
    const { refs } = this.state;
    try {
      const statusList = await Promise.all(
        refs.map(async (singleEducationCard: any) => {
          // 校验单个教育经历，此处已经校验了包括附近在内的所有信息
          const singleEducationCardValidateRes = await singleEducationCard.validate();
          return singleEducationCardValidateRes;
        }),
      );

      return !statusList.includes(false);
    } catch (error) {
      console.error('表单验证失败:', error);
      return false;
    }
  };

  validateMultiItems = async () => {
    // 创建副本以避免修改原始数据
    const { degree: originalDegree, isIntern } = this.state;
    const degree = cloneDeep(originalDegree);

    // 使用抽离出来的工具函数进行多段教育经历校验
    return validateMultiEducations(degree, isIntern);
  };

  /**
   * 前端校验
   * @returns 校验是否通过
   */
  validateFrontEnd = async () => {
    // 单段教育经历内部校验
    const validateEverySingleRes = await this.validateItems();

    // 多段教育经历联动校验
    const validateMultiRes = await this.validateMultiItems();

    // 有的校验不需要卡在保存，有的必须卡
    if (!validateEverySingleRes || !validateMultiRes) {
      return false;
    }

    return true;
  };

  /**
   * 保存教育信息
   * @returns {Promise<boolean>} 保存是否成功
   * handleSave在最外层聚合页使用了
   */
  handleSave = async () => {
    // 前端校验
    const validateFrontEndRes = await this.validateFrontEnd();
    if (!validateFrontEndRes) {
      return false;
    }

    // 获取数据
    const formData = await this.handleFetchFinalData();
    // V3.0 需求升级点击下一步的保存接口为V2版本
    const data = await doAction(Url.saveV2, formData);
    const { pageList = [] } = data || {};

    // V4.2.28 后端控制直博情况，不需要强要求硕士学历，只需要本科（大专）和博士学历
    // 如果后端校验列表不为空，则展示校验列表
    if (pageList.length > 0) {
      pageList.forEach((msg) => {
        notification.error({
          title: '',
          message: msg,
          duration: 5000,
        });
      });
      return false;
    }

    // 还有原来的一些旧的校验逻辑，不改动
    return data === undefined ? false : data;
  };

  /**
   * 获取最终组织的数据
   * @returns 最终组织的数据
   */
  handleFetchFinalData = async () => {
    const { degree, supplementAttachList, employeeType, refs } = this.state;

    // 这里获取的 results 是实时数据，外部组件 state 中的 degree 是上一次渲染的数据
    const results = await Promise.all(
      refs.map(async (singleEducationCard: any) => {
        const { certificateGroupRef } = singleEducationCard;
        // certificateGroupRef.current存在，才是正确的数据
        // 因为每次渲染都把 handleFetchRef 执行了一次，refs的数组越来越多，这是旧逻辑，不好改
        // 但是长度可能有一些多余的，这里再次过滤掉
        if (certificateGroupRef.current) {
          // 校验单个教育经历，此处已经校验了包括附近在内的所有信息
          const singleEducationCardValidateRes = await singleEducationCard.getAllFormData();
          return singleEducationCardValidateRes;
        }

        return null;
      }),
    );

    const filterResults = results.filter((item) => item !== null);

    // 处理实时数据的 attachmentList 字段，需要将非最高学历的附件数据删除，并添加 index 字段
    const degreeData = filterResults?.map((item, index) => {
      const {
        isTopDegree,
        // 附件列表
        attachmentList,
        degreeCode,
        educationCode,
        ...rest
      } = item;

      // 将非最高学历的附件数据删除
      if (!isTopDegree) {
        return {
          isTopDegree,
          index,
          ...rest,
        };
      }

      // 对于 attachmentList ，需要进行数据的处理，有两种异常情况
      const adapterAttachmentList = attachmentList
        ?.map((attachment) => {
          const { unableUpload = false, unableUploadReason, attachmentUrl } = attachment || {};
          // 1、如果没有 unableUpload 字段 或者为false，那么说明是一个全新的数据，没有勾选，或者切换了一下，没有勾选，
          // 如果没有保存文件，那么 attachmentUrl 是空，就直接保存了，需要删掉这个对象，
          // 但是如果 保存了文件，那么 attachmentUrl 是有的，但是 unableUpload 可能为false，所以需要保留这个对象
          // 因此，先默认给一个false，再判断是否传了文件，如果文件没了，就需要删掉这个对象
          if (!unableUpload && !attachmentUrl) {
            return null;
          }

          // 2、如果 unableUpload 为 true ，但是既没有原因，又没有文件，说明只是勾选了，但是没有后续操作，也删除，下次默认刷回去即可
          if (!unableUploadReason && !attachmentUrl) {
            return null;
          }

          return attachment;
        })
        // 过滤掉 null ，只保留没有问题的数据
        .filter((attachment) => attachment !== null);

      // 最高学历的附件数据保留
      return {
        ...item,
        index,
        attachmentList: adapterAttachmentList,
      };
    });

    // 处理实时数据的 isTopDegree 字段
    const adapterDegree = handleAdapterTopDegree(degreeData);

    // 将补充材料和员工类型，与实时的 adapterDegree 数据合并成最终提交的数据
    const finalData = await {
      supplementAttachList,
      degree: adapterDegree,
      employeeType,
    };

    // 此时的degree，可能还没更新，CertificateUploadGroup太内部的数据只是在页面上展示了最新的，但是attachmentFileType还没更新
    // 所以上传的时候，需要使用最新的数据，但是需要将state的degree更新为最新的数据
    const tempDegree = cloneDeep(degree);
    if (JSON.stringify(tempDegree) !== JSON.stringify(filterResults)) {
      this.setState({ degree: filterResults });
    }

    return finalData;
  };

  /**
   * 获取ref
   * @param target ref目标
   */
  handleFetchRef = (target, index) => {
    const { refs } = this.state;
    refs[index] = target;
    this.setState({ refs });
  };

  /**
   * 处理补充材料变更
   */
  handleSupplementaryChange = (supplementaryFile: SupplementaryFile[]) => {
    this.setState({
      supplementAttachList: supplementaryFile,
    });
  };

  render() {
    // TODO解决问题 Warning: EducationInfo: `ref` is not a prop. Trying to access it will result in `undefined` being returned. If you need to access the same value within the child component, you should pass it as a different prop.
    const { ref } = this.props;
    const { degree, isIntern, supplementAttachList, unableReasonList } = this.state;

    return (
      <div className="education" ref={ref}>
        <Announcement
          title="学历的开始和结束时间等信息将影响您的背调结果和正常入职，请按照学历证书、学位证书填写。"
          type="warning"
          showIcon
        />
        {degree.map((item, index) => (
          // 尝试修改 educationKey ，以前这样写不太好
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={educationKey + index}>
            <EducationCard
              unableReasonList={unableReasonList}
              isIntern={isIntern}
              onChange={this.handleChange}
              onDelete={this.handleDelete}
              data={item}
              index={index}
              // 因为以前每次渲染都把 handleFetchRef 执行了一次，refs的数组越来越多，这是旧逻辑，不好改
              // 新增 index ，根据 index 来更新 refs 数组，减少 refs 数组的长度
              getRef={(target) => this.handleFetchRef(target, index)}
            />
            {index < degree.length - 1 && <div className="base-form__dividing" />}
          </React.Fragment>
        ))}

        <span className="education__add" onClick={this.handleAdd}>
          <Icon type="add" />
          教育经历
        </span>

        {/* 补充材料组件 */}
        <Supplementary supplementAttachList={supplementAttachList} onChange={this.handleSupplementaryChange} />
      </div>
    );
  }
}

export default EducationInfo;
